import { PureComponent, type ReactNode } from "react";

import CheckboxMultiSelect from "./CheckboxMultiSelect/CheckboxMultiSelect.js";
import { type IOptionObject } from "./CheckboxMultiSelect/Option.js";

export interface ICheckboxSelectProps {
  uniqueKey: string;
  onChange: (option: IOptionObject[], object: IOptionObject) => void;
  value?: IOptionObject[];
  numberDisplayed?: number;
  valueKey?: string;
  labelKey?: string;
  showFilter?: boolean;
  showSelectAll?: boolean;
  placeholder?: string;
  options?: IOptionObject[];
  allSelectedPlaceholder?: string;
  manySelectedPlaceholder?: string;
  defaultFocusFilter?: boolean;
  renderItemLabel?: boolean;
}

/**
 * This renders a drop-down menu with checkboxes. One or more of the checkboxes can be selected.
 * The difference with CheckboxMultiSelect.tsx is that this component uses our standard input elements for checkboxes etc.
 */
class CheckboxSelect extends PureComponent<ICheckboxSelectProps> {
  renderSelectAll = (props): ReactNode => {
    const { filtered, allSelected, toggleSelectAll, multiple } = props;

    if (multiple && !filtered) {
      return (
        <li>
          <div className="checkbox select-all">
            <input
              type="checkbox"
              id={`checkbox-selectAll-${this.props.uniqueKey}`}
              checked={allSelected}
              onChange={toggleSelectAll}
            />
            <label htmlFor={`checkbox-selectAll-${this.props.uniqueKey}`}>Select all</label>
          </div>
        </li>
      );
    } else {
      return null;
    }
  };

  renderItem = ({ style, isSelected, item, selectValue, labelKey, valueKey }): ReactNode => (
    <li style={style} key={item[valueKey]} onClick={() => selectValue(item)}>
      <div className="checkbox">
        <input
          type="checkbox"
          id={"checkbox-" + item[valueKey]}
          checked={isSelected}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onChange={(e) => e.stopPropagation()}
        />
        <label htmlFor={"checkbox-" + item[valueKey]}>{item[this.props.renderItemLabel ? labelKey : valueKey]}</label>
      </div>
    </li>
  );

  render(): ReactNode {
    return (
      <CheckboxMultiSelect
        id={this.props.uniqueKey}
        render={this.renderItem}
        renderSelectAll={this.props.showSelectAll ? this.renderSelectAll : undefined}
        includeFilter={this.props.showFilter}
        value={this.props.value}
        numberDisplayed={this.props.numberDisplayed}
        valueKey={this.props.valueKey}
        labelKey={this.props.labelKey}
        placeholder={this.props.placeholder}
        options={this.props.options}
        multiple={true}
        onChange={this.props.onChange}
        allSelectedPlaceholder={this.props.allSelectedPlaceholder}
        manySelectedPlaceholder={this.props.manySelectedPlaceholder}
        defaultFocusFilter={this.props.defaultFocusFilter}
      />
    );
  }
}

export default CheckboxSelect;
